import axios from 'axios';

/**
 * Serialize params for Spatie QueryBuilder format.
 * Converts { filter: { status: 'value' } } to 'filter[status]=value'
 *
 * @param {Object} params
 * @returns {string}
 */
function serializeParams(params) {
    const parts = [];

    function serialize(obj, prefix = '') {
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const value = obj[key];
                const paramKey = prefix ? `${prefix}[${key}]` : key;

                if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
                    serialize(value, paramKey);
                } else if (value !== undefined && value !== null && value !== '') {
                    parts.push(`${encodeURIComponent(paramKey)}=${encodeURIComponent(value)}`);
                }
            }
        }
    }

    serialize(params);
    return parts.join('&');
}

/**
 * API Service for the Orders Table Vue component.
 *
 * Centralizes all API calls for the orders table, making Vue components
 * cleaner and easier to maintain.
 *
 * @example
 * import OrdersTableApi from '@js/services/OrdersTableApi';
 *
 * // In a Vue component
 * const response = await OrdersTableApi.getOrders({ page: 1, perPage: 10 });
 * const orders = response.data.data;
 */
const OrdersTableApi = {
    /**
     * Get paginated orders for the table.
     *
     * @param {Object} params - Query parameters
     * @param {number} [params.page=1] - Page number
     * @param {number} [params.perPage=10] - Items per page
     * @param {Object} [params.filter] - Filter options
     * @param {string} [params.filter.status] - Filter by status
     * @param {string} [params.filter.search] - Search keyword
     * @param {number} [params.filter.team_id] - Filter by team
     * @param {boolean} [params.filter.approval_needed] - Filter approval needed
     * @param {string} [params.sort] - Sort field (prefix with - for desc)
     * @returns {Promise<AxiosResponse>}
     */
    getOrders(params = {}) {
        return axios.get('/api/orders-table', {
            params,
            paramsSerializer: serializeParams,
        });
    },

    /**
     * Get a single order by ID.
     *
     * @param {number} orderId - Order ID
     * @returns {Promise<AxiosResponse>}
     */
    getOrder(orderId) {
        return axios.get(`/api/orders-table/${orderId}`);
    },

    /**
     * Update order status.
     *
     * @param {number} orderId - Order ID
     * @param {Object} data - Status update data
     * @param {string} data.status - New status
     * @param {string} [data.status_reason] - Reason for status change
     * @param {string} [data.status_notes] - Additional notes
     * @returns {Promise<AxiosResponse>}
     */
    updateOrderStatus(orderId, data) {
        return axios.put(`/api/orders-table/${orderId}/status`, data);
    },

    /**
     * Update order item status for multiple items.
     *
     * Used for bulk operations like marking items as fulfilled/unfulfilled.
     *
     * @param {number} orderId - Order ID
     * @param {Object} data - Status update data
     * @param {number[]} data.ids - Array of order item IDs
     * @param {string} data.status - New status for the items
     * @returns {Promise<AxiosResponse>}
     */
    updateOrderItemStatus(orderId, data) {
        return axios.put(`/api/orders-table/${orderId}/items/status`, data);
    },

    /**
     * Get status counts for the status key component.
     *
     * @returns {Promise<AxiosResponse>}
     */
    getStatusCounts() {
        return axios.get('/api/orders-table/status-counts');
    },

    /**
     * Search orders using the specialized search endpoint.
     *
     * This endpoint uses OrderSearchRepository for combined order/item searches.
     *
     * @param {Object} params - Query parameters
     * @param {number} [params.page=1] - Page number
     * @param {number} [params.perPage=10] - Items per page
     * @param {string} [params.allowedStatuses] - Comma-separated list of allowed status classes
     * @param {Object} [params.filter] - Filter options
     * @param {string} [params.filter.status] - Status filter (Pending, Active, Complete, Verified, Denied)
     * @param {string} [params.filter.keyword] - Search keyword
     * @param {string} [params.filter.date] - Date range filter
     * @param {string} [params.sort] - Sort field (prefix with - for desc)
     * @returns {Promise<AxiosResponse>}
     */
    searchOrders(params = {}) {
        return axios.get('/api/orders-table/search', {
            params,
            paramsSerializer: serializeParams,
        });
    },

    /**
     * Get paginated order items for the order items table.
     *
     * This endpoint is used for displaying individual order items (e.g., in denied/expired views).
     *
     * @param {Object} params - Query parameters
     * @param {number} [params.page=1] - Page number
     * @param {number} [params.perPage=10] - Items per page
     * @param {Object} [params.filter] - Filter options (supports OrderItemRepository filters)
     * @param {string} [params.sort] - Sort field (prefix with - for desc)
     * @returns {Promise<AxiosResponse>}
     */
    getOrderItems(params = {}) {
        return axios.get('/api/orders-table/order-items', {
            params,
            paramsSerializer: serializeParams,
        });
    },
};

export default OrdersTableApi;
